/**
 * Central registry for all playground examples.
 *
 * To add a new example:
 * 1. Create a file in src/examples/data/my-example.ts exporting an `Example` object.
 * 2. Import it here and add it to the EXAMPLES array below.
 * 3. Add a subcategory (and top-level category) here if needed.
 *
 * Rules:
 * - No subcategory should exceed 10 examples (keeps the dropdown panel short).
 * - EXAMPLES order determines the prev/next slideshow order.
 */

import type { Example, ExampleCategory, ExampleSubcategory } from './types';

// ── Individual example imports ────────────────────────────────────────────
import { parameterTypesExample } from './data/parameter-types';
import { visibilityExpressionsExample } from './data/visibility-expressions';
import { parameterSnapshotsExample } from './data/parameter-snapshots';
import { markdownCalloutsExample } from './data/markdown-callouts';
import { colorPickerExample } from './data/color-picker';
import { angleDialExample } from './data/angle-dial';
import { stepperGridStackExample } from './data/stepper-grid-stack';
import { dynamicRangeLimitsExample } from './data/dynamic-range-limits';
import { vectorSlidersExample } from './data/vector-sliders';
import { cameraPipExample } from './data/camera-pip';
import { pbrMaterialsExample } from './data/pbr-materials';
import { sceneGridExample } from './data/scene-grid';
import { motionAnimationExample } from './data/motion-animation';
import { ghostTransparencyExample } from './data/ghost-transparency';
import { lightingSystemExample } from './data/lighting-system';
import { animatedLightsExample } from './data/animated-lights';
import { postProcessingExample } from './data/post-processing';
import { meshClonesExample } from './data/mesh-clones';
import { annotationsInteractiveExample } from './data/annotations-interactive';
import { introCameraPathExample } from './data/intro-camera-path';

// ── Top-level categories ──────────────────────────────────────────────────
// Each becomes one NavigationMenu trigger in the toolbar.

export const EXAMPLE_CATEGORIES: ExampleCategory[] = [
  { id: 'parameters', label: 'Parameters' },
  { id: 'viewer',     label: 'Viewer' },
];

// ── Subcategories ─────────────────────────────────────────────────────────
// Shown as group headings inside each category's dropdown panel.
// Keep each subcategory to ≤ 10 examples.

export const EXAMPLE_SUBCATEGORIES: ExampleSubcategory[] = [
  // Parameters
  { id: 'param-widgets',   categoryId: 'parameters', label: 'Widgets' },
  { id: 'param-behaviour', categoryId: 'parameters', label: 'Behaviour' },
  // Viewer
  { id: 'viewer-cameras',   categoryId: 'viewer', label: 'Cameras & Scene' },
  { id: 'viewer-lighting',  categoryId: 'viewer', label: 'Materials & Lighting' },
  { id: 'viewer-objects',   categoryId: 'viewer', label: 'Objects & Motion' },
];

// ── Examples ──────────────────────────────────────────────────────────────
// Each entry maps to a route: /playground/examples/:id

export const EXAMPLES: Example[] = [
  // ── Parameters: Widgets ─────────────────────────────────────────────────
  { ...parameterTypesExample,    subcategoryId: 'param-widgets' },
  { ...colorPickerExample,       subcategoryId: 'param-widgets' },
  { ...angleDialExample,         subcategoryId: 'param-widgets' },
  { ...vectorSlidersExample,     subcategoryId: 'param-widgets' },
  { ...stepperGridStackExample,  subcategoryId: 'param-widgets' },

  // ── Parameters: Behaviour ───────────────────────────────────────────────
  { ...visibilityExpressionsExample, subcategoryId: 'param-behaviour' },
  { ...parameterSnapshotsExample,    subcategoryId: 'param-behaviour' },
  { ...markdownCalloutsExample,      subcategoryId: 'param-behaviour' },
  { ...dynamicRangeLimitsExample,    subcategoryId: 'param-behaviour' },

  // ── Viewer: Cameras & Scene ─────────────────────────────────────────────
  { ...cameraPipExample,         subcategoryId: 'viewer-cameras' },
  { ...introCameraPathExample,   subcategoryId: 'viewer-cameras' },
  { ...sceneGridExample,         subcategoryId: 'viewer-cameras' },
  { ...ghostTransparencyExample, subcategoryId: 'viewer-cameras' },

  // ── Viewer: Materials & Lighting ────────────────────────────────────────
  { ...pbrMaterialsExample,      subcategoryId: 'viewer-lighting' },
  { ...lightingSystemExample,    subcategoryId: 'viewer-lighting' },
  { ...animatedLightsExample,    subcategoryId: 'viewer-lighting' },
  { ...postProcessingExample,    subcategoryId: 'viewer-lighting' },

  // ── Viewer: Objects & Motion ────────────────────────────────────────────
  { ...motionAnimationExample,        subcategoryId: 'viewer-objects' },
  { ...meshClonesExample,             subcategoryId: 'viewer-objects' },
  { ...annotationsInteractiveExample, subcategoryId: 'viewer-objects' },
];

// ── Helpers ───────────────────────────────────────────────────────────────

/** Look up a single example by its id slug. */
export function getExampleById(id: string): Example | undefined {
  return EXAMPLES.find((e) => e.id === id);
}

/** All examples that belong to a given top-level category. */
export function getExamplesByCategory(categoryId: string): Example[] {
  return EXAMPLES.filter((e) => e.categoryId === categoryId);
}

/** All examples that belong to a given subcategory. */
export function getExamplesBySubcategory(subcategoryId: string): Example[] {
  return EXAMPLES.filter((e) => e.subcategoryId === subcategoryId);
}

/**
 * Categories with their subcategories and examples, ready to drive the nav menu.
 * Categories/subcategories with no examples are omitted.
 */
export function getCategoriesWithSubcategories(): Array<{
  category: ExampleCategory;
  subcategories: Array<{
    subcategory: ExampleSubcategory;
    examples: Example[];
  }>;
}> {
  return EXAMPLE_CATEGORIES.flatMap((category) => {
    const subcategories = EXAMPLE_SUBCATEGORIES.filter(
      (s) => s.categoryId === category.id,
    ).flatMap((subcategory) => {
      const examples = getExamplesBySubcategory(subcategory.id);
      if (examples.length === 0) return [];
      return [{ subcategory, examples }];
    });
    if (subcategories.length === 0) return [];
    return [{ category, subcategories }];
  });
}

/** Total count of all examples. */
export function getExampleCount(): number {
  return EXAMPLES.length;
}

/** Get the next example in the flat list, wrapping around. */
export function getNextExample(currentId: string): Example | undefined {
  if (EXAMPLES.length === 0) return undefined;
  const idx = EXAMPLES.findIndex((e) => e.id === currentId);
  if (idx === -1) return EXAMPLES[0];
  return EXAMPLES[(idx + 1) % EXAMPLES.length];
}

/** Get the previous example in the flat list, wrapping around. */
export function getPrevExample(currentId: string): Example | undefined {
  if (EXAMPLES.length === 0) return undefined;
  const idx = EXAMPLES.findIndex((e) => e.id === currentId);
  if (idx === -1) return EXAMPLES[0];
  return EXAMPLES[(idx - 1 + EXAMPLES.length) % EXAMPLES.length];
}

// ── Legacy helper (kept for any existing callers) ─────────────────────────

/** @deprecated Use getCategoriesWithSubcategories() instead. */
export function getCategoriesWithExamples(): Array<{
  category: ExampleCategory;
  examples: Example[];
}> {
  return EXAMPLE_CATEGORIES.flatMap((category) => {
    const examples = getExamplesByCategory(category.id);
    if (examples.length === 0) return [];
    return [{ category, examples }];
  });
}
